export const BACKEND_COURSES = [
  {
    id: 'nodejs-fundamentals',
    title: 'Node.js Fundamentals',
    description: 'Master server-side JavaScript with Node.js. Build APIs, handle async operations, and work with databases.',
    level: 'beginner',
    totalDuration: '8-10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['JavaScript fundamentals', 'Basic command line knowledge'],
    learningOutcomes: [
      'Understand Node.js runtime and event loop',
      'Build REST APIs with Express.js',
      'Handle file systems and streams',
      'Work with NPM and package management',
      'Implement authentication and middleware',
      'Connect to databases (MongoDB, PostgreSQL)'
    ],
    modules: [
      {
        id: 'nodejs-intro',
        title: 'Node.js Introduction & Setup',
        description: 'Getting started with Node.js runtime and understanding its architecture',
        duration: '90 minutes',
        difficulty: 'beginner',
        objectives: [
          'Understand Node.js architecture and event loop',
          'Set up Node.js development environment',
          'Create your first Node.js application',
          'Learn about modules and require system'
        ],
        content: `# Node.js Introduction & Setup

## What is Node.js?

Node.js is a JavaScript runtime built on Chrome's V8 JavaScript engine. It allows you to run JavaScript on the server side.

### Key Features:
- **Event-driven**: Non-blocking I/O operations
- **Single-threaded**: Uses event loop for concurrency
- **Fast**: Built on V8 engine
- **NPM**: Vast ecosystem of packages

## Installation & Setup

\`\`\`bash
# Install Node.js (using Node Version Manager)
curl -o- https://raw.githubusercontent.com/nvm-sh/nvm/v0.39.0/install.sh | bash
nvm install node
nvm use node

# Verify installation
node --version
npm --version
\`\`\`

## Your First Node.js Application

\`\`\`javascript
// app.js
console.log('Hello, Node.js!');

// HTTP Server
const http = require('http');

const server = http.createServer((req, res) => {
  res.writeHead(200, { 'Content-Type': 'text/plain' });
  res.end('Hello from Node.js server!');
});

server.listen(3000, () => {
  console.log('Server running on http://localhost:3000');
});
\`\`\`

## Modules and Require

\`\`\`javascript
// math.js
function add(a, b) {
  return a + b;
}

function multiply(a, b) {
  return a * b;
}

module.exports = { add, multiply };

// app.js
const { add, multiply } = require('./math');

console.log(add(2, 3)); // 5
console.log(multiply(4, 5)); // 20
\`\`\``,
        practice: {
          task: 'Create a simple HTTP server that serves different responses based on the URL path',
          starter: `const http = require('http');

// Create your server here`,
          hints: [
            'Use req.url to get the requested path',
            'Use switch statement to handle different routes',
            'Set appropriate content types for different responses'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'nodejs-1',
              question: 'What makes Node.js different from browser JavaScript?',
              options: [
                'It uses a different JavaScript engine',
                'It can access file system and network',
                'It only supports ES5 syntax',
                'It requires compilation'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'Node.js provides APIs to access file system, network, and other system resources not available in browsers.',
              points: 10
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'backend'
  },
  {
    id: 'django-web-development',
    title: 'Django Web Development',
    description: 'Build robust web applications with Django. Learn MVT architecture, ORM, authentication, and deployment.',
    level: 'intermediate',
    totalDuration: '12-15 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Python fundamentals', 'Basic web development concepts', 'SQL basics'],
    learningOutcomes: [
      'Master Django MVT architecture',
      'Build dynamic web applications',
      'Implement user authentication and authorization',
      'Work with Django ORM and databases',
      'Create RESTful APIs with Django REST Framework',
      'Deploy Django applications to production'
    ],
    modules: [
      {
        id: 'django-setup',
        title: 'Django Project Setup',
        description: 'Setting up Django development environment and creating your first project',
        duration: '75 minutes',
        difficulty: 'beginner',
        objectives: [
          'Install and configure Django',
          'Create Django projects and apps',
          'Understand Django project structure',
          'Run development server'
        ],
        content: `# Django Project Setup

## Installation

\`\`\`bash
# Create virtual environment
python -m venv django_env
source django_env/bin/activate  # On Windows: django_env\\Scripts\\activate

# Install Django
pip install django

# Verify installation
django-admin --version
\`\`\`

## Creating Your First Project

\`\`\`bash
# Create project
django-admin startproject myproject
cd myproject

# Create an app
python manage.py startapp myapp

# Run development server
python manage.py runserver
\`\`\`

## Project Structure

\`\`\`
myproject/
    manage.py
    myproject/
        __init__.py
        settings.py
        urls.py
        wsgi.py
        asgi.py
    myapp/
        __init__.py
        admin.py
        apps.py
        models.py
        tests.py
        views.py
        migrations/
\`\`\`

## Settings Configuration

\`\`\`python
# settings.py
INSTALLED_APPS = [
    'django.contrib.admin',
    'django.contrib.auth',
    'django.contrib.contenttypes',
    'django.contrib.sessions',
    'django.contrib.messages',
    'django.contrib.staticfiles',
    'myapp',  # Add your app here
]

DATABASES = {
    'default': {
        'ENGINE': 'django.db.backends.sqlite3',
        'NAME': BASE_DIR / 'db.sqlite3',
    }
}
\`\`\``,
        practice: {
          task: 'Create a Django project with a blog app that displays a simple "Hello World" page',
          starter: `# Create your Django project and configure it`,
          hints: [
            'Use django-admin startproject command',
            'Create a blog app using manage.py startapp',
            'Add the app to INSTALLED_APPS in settings.py',
            'Create a simple view function and URL pattern'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'django-1',
              question: 'What command is used to create a new Django project?',
              options: [
                'django-admin createproject',
                'django-admin startproject',
                'python manage.py startproject',
                'django startproject'
              ],
              correctAnswer: 1,
              difficulty: 'easy',
              explanation: 'django-admin startproject is the correct command to create a new Django project.',
              points: 10
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'backend'
  }
];